import Router from 'next/router';
import { useCallback } from 'react';

import { useTrackCanvasCreated, useTrackCanvasDraftCreated } from '@air/analytics';
import { useChatSidebarStore } from '@air/feature-ag-ui-chat';
import {
  DEFAULT_CANVAS_TITLE,
  type PendingCreateNode,
  preloadImageNodes,
  type ResolveCanvasIdResult,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';

import { useCanvasVersionTargetBarDismissedStore } from '~/components/Canvas/store/useCanvasVersionTargetBarDismissedStore';
import { useCanvasVersionTargetSessionStore } from '~/components/Canvas/store/useCanvasVersionTargetSessionStore';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { type DraftCanvasState } from '~/components/v2/store/shared/createDraftCanvasSlice';
import { useDiscardDraftCanvas } from '~/hooks/canvases/useDiscardDraftCanvas';
import { useRecordCanvasView } from '~/hooks/canvases/useRecordCanvasView';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getCanvasDetailKey } from '~/swr-hooks/canvases/useCanvas';
import { useCreateCanvas } from '~/swr-hooks/canvases/useCreateCanvas';
import { queryClient } from '~/swr-hooks/queryClient';
import { morphAssetModalImageToCanvas } from '~/utils/canvases/morphAssetModalHero';
import {
  getAssetModalParentHref,
  getClipIdFromPath,
  getPrivateCanvasDraftHref,
  getPrivateCanvasIdFromPath,
  getPrivateCanvasPageHref,
  getPrivateCanvasParentHref,
} from '~/utils/PathUtils';

// Module-level promise for commit deduplication. Multiple components call useDraftCanvas(),
// each getting their own hook instance. Without a shared variable, concurrent commits from
// different components (e.g. sync queue flush + thread creation) would each create a canvas.
let commitPromise: Promise<ResolveCanvasIdResult> | null = null;

/**
 * Hook for draft canvas mode — defers backend canvas creation until the user
 * takes a meaningful action (adds a node, starts a chat thread).
 *
 * `openDraftCanvas` opens the dedicated canvas page in draft mode without creating a backend record.
 * `commitDraftCanvas` creates the backend canvas and fires deferred analytics.
 * `discardDraft` cleans up draft state when the page is closed without committing.
 */
export const useDraftCanvas = () => {
  const draftCanvas = useWorkspaceRightPanelStore((s) => s.draftCanvas);
  const { mutateAsync: createCanvas } = useCreateCanvas();
  const { trackCanvasCreated } = useTrackCanvasCreated();
  const { trackCanvasDraftCreated } = useTrackCanvasDraftCreated();
  const discardDraftCanvas = useDiscardDraftCanvas();
  const { recordView } = useRecordCanvasView();

  const { currentWorkspace } = useCurrentWorkspace();

  const openDraftCanvas = useCallback(
    (params: DraftCanvasState) => {
      // Fire abandonment analytics if replacing an existing draft. Do not also
      // null selection here — that restores the board list and paints the prior
      // canvas for a frame before the empty-canvas hero mounts.
      discardDraftCanvas();
      // Read before the canvas push. asPath is still the asset modal on this turn.
      const assetModalPath = getClipIdFromPath(Router.asPath) ? Router.asPath.split('#')[0] : null;
      useWorkspaceRightPanelStore.getState().setCanvasReturnTo(assetModalPath);

      if (assetModalPath) {
        void Router.prefetch(assetModalPath);
      }
      // EditCanvas syncs canvasId in an effect, so drop leftover nodes in this
      // click before React paints. `setCanvasId('')` is a no-op when already on
      // a draft (`''`), which would keep Edit-in-canvas assets and skip the hero.
      useEditCanvasStore.getState().resetForNewDraft();
      const openKey = crypto.randomUUID();
      useWorkspaceRightPanelStore.getState().startDraftCanvas({ ...params, openKey });
      useChatSidebarStore.getState().setOpen(false);
      trackCanvasDraftCreated(params.analyticsContext);

      // The asset modal stays up until the image nodes can paint on the canvas's first frame.
      void preloadImageNodes(params.initialNodes).then(() => {
        const { canvasReturnTo, draftCanvas } = useWorkspaceRightPanelStore.getState();
        const stillOpeningThisDraft = draftCanvas?.openKey === openKey;
        const assetModalStillOpen = assetModalPath == null || canvasReturnTo === assetModalPath;

        if (stillOpeningThisDraft && assetModalStillOpen) {
          const href = getPrivateCanvasDraftHref(params.boardId ? { boardId: params.boardId } : undefined);
          morphAssetModalImageToCanvas(() => void Router.push(href));
        }
      });
    },
    [discardDraftCanvas, trackCanvasDraftCreated],
  );

  const commitDraftCanvas = useCallback(
    async (pendingCreates: PendingCreateNode[] = [], title = DEFAULT_CANVAS_TITLE): Promise<ResolveCanvasIdResult> => {
      const draft = useWorkspaceRightPanelStore.getState().draftCanvas;

      if (!draft) {
        throw new Error('No draft canvas to commit');
      }

      // Deduplicate: if a commit is already in progress, return the same promise
      if (commitPromise) {
        return commitPromise;
      }

      commitPromise = (async () => {
        useWorkspaceRightPanelStore.getState().setSkipPostCommitNavigation(false);

        try {
          // Signal that we're transitioning — prevents store clearing in useCanvasForRightPanel
          useWorkspaceRightPanelStore.getState().setCommittingDraft(true);

          // Bundle pending node creates with canvas creation
          const nodes =
            pendingCreates.length > 0
              ? pendingCreates.map(({ params }) => ({
                  name: params.name,
                  positionX: params.position.x,
                  positionY: params.position.y,
                  width: params.width,
                  height: params.height,
                  zIndex: params.zIndex,
                  data: params.data,
                }))
              : undefined;

          const canvas = await createCanvas({
            title,
            boardId: draft.boardId,
            nodes,
          });

          // Pre-populate React Query cache to avoid refetch jank
          const workspaceId = currentWorkspace?.id;

          if (workspaceId) {
            queryClient.setQueryData(getCanvasDetailKey({ canvasId: canvas.id, workspaceId }), canvas);
          }

          // Before the URL gains the canvas id, so the leave listener sees this canvas as the one still shown.
          if (draft.openKey) {
            useCanvasVersionTargetBarDismissedStore.getState().rekey(draft.openKey, canvas.id);
          }

          if (draft.versionTargetClipId) {
            useCanvasVersionTargetSessionStore.getState().setLink({
              canvasId: canvas.id,
              clipId: draft.versionTargetClipId,
              runIds: useEditCanvasStore.getState().versionTarget?.linkedRunIds ?? [],
            });
          }

          // Transition from draft to real canvas.
          // Keep committingDraft=true through the re-render so useCanvasForRightPanel
          // skips store clearing. React 18 schedules passive effects as macrotasks via
          // MessageChannel, so requestAnimationFrame (which fires during the browser's
          // rendering phase, after macrotasks) ensures the flag is still true when effects read it.
          // Attribution must land before replace: PrivateCanvasEditor tracks viewed_from
          // once per canvas id, so a later setSelectedCanvasId is ignored.
          useWorkspaceRightPanelStore.getState().setSelectedCanvasId(canvas.id, draft.viewedFrom);
          // Keep draft + commitPromise until replace finishes so a sync flush on /canvases/new
          // joins this create instead of opening a second canvas.
          try {
            if (!useWorkspaceRightPanelStore.getState().skipPostCommitNavigation) {
              await Router.replace(
                getPrivateCanvasPageHref(canvas.id, draft.boardId ? { boardId: draft.boardId } : undefined),
              );
            }
          } catch {
            // Create already succeeded. Swallow so catch/finally cannot retry Canvases.create.
          }

          // Close already left for the parent page. If commit's replace still landed on this
          // canvas, follow that same parent — canvasReturnTo would reopen the asset modal.
          if (useWorkspaceRightPanelStore.getState().skipPostCommitNavigation) {
            const { canvasReturnTo, setCanvasReturnTo } = useWorkspaceRightPanelStore.getState();

            if (getPrivateCanvasIdFromPath(Router.asPath) === canvas.id) {
              try {
                await Router.replace(
                  canvasReturnTo ? getAssetModalParentHref(canvasReturnTo) : getPrivateCanvasParentHref(Router.asPath),
                );
              } catch {
                // Create already succeeded; a failed parent replace is better than retrying create.
              }
            }

            if (canvasReturnTo) {
              setCanvasReturnTo(null);
            }
          }

          useWorkspaceRightPanelStore.getState().setDraftCanvas(null);

          recordView(canvas.id);

          requestAnimationFrame(() => {
            useWorkspaceRightPanelStore.getState().setCommittingDraft(false);
          });

          // Fire deferred analytics with original attribution
          trackCanvasCreated({
            canvas_id: canvas.id,
            ...draft.analyticsContext,
          });

          // Map response node IDs back to local IDs for remoteId assignment.
          // The API returns nodes in the same order they were sent, so index-based mapping is safe.
          // Only include IDs that actually received remote IDs — if the backend returns fewer
          // nodes than sent, excess creates must remain in the sync queue for retry.
          const count =
            canvas.nodes && pendingCreates.length > 0 ? Math.min(canvas.nodes.length, pendingCreates.length) : 0;
          const createdNodeLocalIds =
            count > 0 ? pendingCreates.slice(0, count).map(({ localId }) => localId) : undefined;

          for (let i = 0; i < count; i++) {
            useEditCanvasStore.getState().updateNodeRemoteId(pendingCreates[i].localId, canvas.nodes[i].id);
          }

          return { canvasId: canvas.id, createdNodeLocalIds };
        } catch (error) {
          useWorkspaceRightPanelStore.getState().setCommittingDraft(false);
          throw error;
        } finally {
          commitPromise = null;
          useWorkspaceRightPanelStore.getState().setSkipPostCommitNavigation(false);
        }
      })();

      return commitPromise;
    },
    [createCanvas, currentWorkspace?.id, recordView, trackCanvasCreated],
  );

  const discardDraft = discardDraftCanvas;

  return {
    draftCanvas,
    isDraft: !!draftCanvas,
    openDraftCanvas,
    commitDraftCanvas,
    discardDraft,
  };
};
